import { useEffect, useState } from 'react';
import { Deal } from '../../models/shared/Deal';
import { Invoice, InvoiceStatus } from '../../models/shared/Invoice';
import { useFirestore } from 'reactfire';
import { doc, updateDoc } from 'firebase/firestore';
import { COLLECTIONS } from '../../utils/shared/constants';
import { useOrganization } from '../../context/OrganizationContext';
import { toast } from 'react-toastify';
import { useText } from '../../context/TextContext';
import { useSaveToast } from '../../context/SaveToastContext';
import { Form, FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import LineItemsEditor from '../LineItemsEditor';
import OrgCustomerCard from '../OrgCustomerCard';
import { formatDate } from '../../utils/date';

interface Props {
    deal: Deal;
    invoice: Invoice;
}

const parseLocalDate = (value: string): Date | null => {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
};

let timeout: NodeJS.Timeout | null = null;

export default function InvoiceEditor(props: Props) {
    const { invoice: originalInvoice, deal } = props;
    const [invoice, setInvoice] = useState<Invoice>(originalInvoice);
    const firestore = useFirestore();
    const { orgId } = useOrganization();
    const text = useText();
    const { markSaved, startSaving, stopSaving } = useSaveToast();

    useEffect(() => {
        setInvoice(originalInvoice);
    }, [originalInvoice]);

    const handleInvoiceUpdate = (update: Partial<Invoice>) => {
        const updatedInvoice = { ...invoice, ...update, updatedAt: new Date() };
        setInvoice(updatedInvoice);

        if (timeout) {
            clearTimeout(timeout);
        }

        timeout = setTimeout(async () => {
            try {
                const saveId = startSaving();

                const invoiceRef = doc(
                    firestore,
                    `${COLLECTIONS.ORGANIZATIONS}/${orgId}/${COLLECTIONS.DEALS}/${deal.id}/${COLLECTIONS.INVOICES}/${invoice.id}`
                );

                await updateDoc(invoiceRef, {
                    ...update,
                    updatedAt: new Date()
                });

                markSaved(saveId);
            } catch (error) {
                console.error('Error updating invoice', error);
                stopSaving();
                toast.error(text.defaultError);
            }
        }, 1000);
    };

    return (
        <div className="d-flex flex-column shadow p-5 rounded bg-offer gap-4">
            <div className="d-flex justify-content-between align-items-center">
                <h4 className="m-0">{text.invoice} {invoice.invoiceNumber ? `#${invoice.invoiceNumber}` : ''}</h4>
            </div>

            <OrgCustomerCard deal={deal} showPaymentInstructions />

            <div className="d-flex gap-4 flex-wrap align-items-start">
                <FormGroup>
                    <FormLabel className="fw-semibold">{text.status}</FormLabel>
                    <Form.Select
                        value={invoice.status}
                        onChange={(e) => handleInvoiceUpdate({ status: e.target.value as InvoiceStatus })}
                    >
                        <option value="draft">{text.invoiceStatusDraft}</option>
                        <option value="sent">{text.invoiceStatusSent}</option>
                        <option value="paid">{text.invoiceStatusPaid}</option>
                        <option value="cancelled">{text.invoiceStatusCancelled}</option>
                    </Form.Select>
                </FormGroup>

                <FormGroup>
                    <FormLabel className="fw-semibold">{text.dueDate}</FormLabel>
                    <FormControl
                        type="date"
                        value={invoice.dueAt ? formatDate(invoice.dueAt, { isoDate: true, noTime: true }) : ''}
                        onChange={(e) => handleInvoiceUpdate({ dueAt: parseLocalDate(e.target.value) })}
                    />
                </FormGroup>

                {invoice.periodStart && invoice.periodEnd && (
                    <div>
                        <div className="fw-semibold">{text.period}</div>
                        <div>{formatDate(invoice.periodStart, { noTime: true })} - {formatDate(invoice.periodEnd, { noTime: true })}</div>
                    </div>
                )}
            </div>

            <FormGroup>
                <FormLabel className="fw-semibold">{text.notes}</FormLabel>
                <FormControl
                    as="textarea"
                    rows={3}
                    value={invoice.notes || ''}
                    onChange={(e) => handleInvoiceUpdate({ notes: e.target.value })}
                />
            </FormGroup>

            <LineItemsEditor
                lineItems={invoice.lineItems || []}
                onLineItemsChange={(lineItems) => handleInvoiceUpdate({ lineItems })}
            />
        </div>
    );
}
